import React, { useState, useEffect, useRef } from "react";
import { JustCallDialer } from "@justcall/justcall-dialer-sdk";
import { FaTimes, FaMinus, FaExpand, FaCompress } from "react-icons/fa";
import "./floating-dialer.css";

const FloatingDialer = ({ phoneNumber, onClose, onCallStatusChange }) => {
  const [dialer, setDialer] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [callStatus, setCallStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [position, setPosition] = useState({ x: window.innerWidth - 420, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const dialerRef = useRef(null);

  useEffect(() => {
    // Initialize JustCall Dialer
    const initializeDialer = () => {
      try {
        const dialerInstance = new JustCallDialer({
          dialerId: "justcall-dialer-iframe-floating",
          onLogin: (data) => {
            console.log("JustCall Logged in:", data);
            setIsInitialized(true);
            setError(null);
          },
          onLogout: () => {
            console.log("JustCall Logged out");
            setIsInitialized(false);
          },
          onReady: () => {
            console.log("JustCall Dialer Ready");
            setIsInitialized(true);
          },
        });

        setDialer(dialerInstance);
      } catch (err) {
        console.error("Failed to initialize JustCall Dialer:", err);
        setError(err.message || "Failed to initialize dialer");
        setIsInitialized(false);
      }
    };

    initializeDialer();

    // Cleanup on unmount
    return () => {
      if (dialer) {
        dialer.destroy();
      }
    };
  }, []);

  const dialPhoneNumber = async () => {
    if (!dialer) {
      setError("Dialer not initialized");
      return;
    }

    if (!phoneNumber) {
      setError("Phone number is required");
      return;
    }

    try {
      setCallStatus("initiating");
      setError(null);

      // Ensure dialer is ready before dialing
      await dialer.ready();

      // Check if user is logged in
      const loggedIn = await dialer.isLoggedIn();
      if (!loggedIn) {
        setError("Please log in to JustCall first");
        setCallStatus("idle");
        return;
      }

      // Dial the number
      dialer.dialNumber(phoneNumber);
      setCallStatus("dialing");
      if (onCallStatusChange) {
        onCallStatusChange("dialing", { phoneNumber });
      }
    } catch (err) {
      console.error("Failed to dial number:", err);
      setError(err.message || "Failed to dial number");
      setCallStatus("idle");
    }
  };

  // Auto-dial when phone number is provided and dialer is ready
  useEffect(() => {
    if (phoneNumber && dialer && isInitialized && callStatus === "idle") {
      const timer = setTimeout(() => {
        dialPhoneNumber();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [phoneNumber, dialer, isInitialized]);

  // Handle mouse down for dragging
  const handleMouseDown = (e) => {
    if (e.target.classList.contains("floating-dialer-header") ||
        e.target.closest(".floating-dialer-header")) {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  // Handle mouse move for dragging
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y,
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const handleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const getSize = () => {
    if (isMinimized) {
      return { width: 300, height: 60 };
    }
    if (isExpanded) {
      return { width: 600, height: 700 };
    }
    return { width: 400, height: 500 };
  };

  const size = getSize();

  return (
    <div
      ref={dialerRef}
      className={`floating-dialer-container ${isMinimized ? "minimized" : ""} ${
        isExpanded ? "expanded" : ""
      }`}
      style={{
        position: "fixed",
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: isMinimized ? `${size.height}px` : `${size.height}px`,
        zIndex: 9999,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: "#fff",
        cursor: isDragging ? "grabbing" : "default",
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Header */}
      <div
        className="floating-dialer-header"
        style={{
          padding: "12px 16px",
          backgroundColor: "#0F337C",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "grab",
          userSelect: "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: callStatus === "dialing" ? "#4caf50" : "#ccc",
            }}
          />
          <span style={{ fontWeight: "600", fontSize: "14px" }}>
            {isMinimized ? "Call Active" : "JustCall Dialer"}
          </span>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={handleMinimize}
            className="floating-dialer-btn"
            title={isMinimized ? "Maximize" : "Minimize"}
            style={{
              background: "transparent",
              border: "none",
              color: "#fff",
              cursor: "pointer",
              padding: "4px 8px",
              borderRadius: "4px",
            }}
          >
            <FaMinus size={14} />
          </button>
          {!isMinimized && (
            <button
              onClick={handleExpand}
              className="floating-dialer-btn"
              title={isExpanded ? "Normal Size" : "Expand"}
              style={{
                background: "transparent",
                border: "none",
                color: "#fff",
                cursor: "pointer",
                padding: "4px 8px",
                borderRadius: "4px",
              }}
            >
              {isExpanded ? <FaCompress size={14} /> : <FaExpand size={14} />}
            </button>
          )}
          <button
            onClick={handleClose}
            className="floating-dialer-btn"
            title="Close"
            style={{
              background: "transparent",
              border: "none",
              color: "#fff",
              cursor: "pointer",
              padding: "4px 8px",
              borderRadius: "4px",
            }}
          >
            <FaTimes size={14} />
          </button>
        </div>
      </div>

      {/* Content - Always rendered but hidden when minimized to preserve iframe */}
      <div
        style={{
          padding: "16px",
          height: "calc(100% - 60px)",
          overflow: "auto",
          display: isMinimized ? "none" : "block"
        }}
      >
        <div className="floating-dialer-status">
          <div className="status-indicator">
            <span
              className={`status-badge ${
                isInitialized ? "initialized" : "not-initialized"
              }`}
              style={{
                padding: "4px 12px",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: "600",
                backgroundColor: isInitialized ? "#e8f5e9" : "#fff3e0",
                color: isInitialized ? "#2e7d32" : "#e65100",
              }}
            >
              {isInitialized ? "Ready" : "Please login to JustCall"}
            </span>
          </div>
        </div>

        {error && (
          <div
            className="error-message"
            style={{
              padding: "8px 12px",
              marginTop: "12px",
              backgroundColor: "#ffebee",
              color: "#c62828",
              borderRadius: "6px",
              fontSize: "13px",
            }}
          >
            <span className="error-icon">⚠️</span> {error}
          </div>
        )}

        {phoneNumber && (
          <div
            className="phone-number-display"
            style={{
              padding: "12px",
              marginTop: "12px",
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              fontSize: "14px",
            }}
          >
            <strong>Calling:</strong> {phoneNumber}
          </div>
        )}

        {callStatus !== "idle" && (
          <div
            className="call-status-display"
            style={{
              padding: "12px",
              marginTop: "12px",
              backgroundColor: "#e3f2fd",
              borderRadius: "8px",
              fontSize: "14px",
            }}
          >
            <strong>Status:</strong>{" "}
            <span className="text-capitalize" style={{ fontWeight: "600" }}>
              {callStatus}
            </span>
          </div>
        )}

        {/* JustCall Dialer Iframe - Always in DOM to maintain SDK connection */}
        <div
          id="justcall-dialer-iframe-floating"
          style={{
            width: "100%",
            minHeight: isExpanded ? "500px" : "300px",
            marginTop: "12px",
          }}
        ></div>
      </div>

      {/* Minimized View */}
      {isMinimized && (
        <div
          style={{
            padding: "8px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "#4caf50",
                animation: "pulse 2s infinite",
              }}
            />
            <span style={{ fontSize: "13px", fontWeight: "500" }}>
              {phoneNumber}
            </span>
          </div>
          <span
            style={{
              fontSize: "12px",
              color: "#666",
              textTransform: "capitalize",
            }}
          >
            {callStatus}
          </span>
        </div>
      )}
    </div>
  );
};

export default FloatingDialer;
